import { useMemo } from 'react'

import { getBlockConfig } from '../templateEditor/BlockConfigs'
import { DisplayBlock as DisplayBlockType } from '../types/blueprint'

import { DisplayAttachments } from './DisplayAttachments'
import { DisplayBlockWrapper, DisplayName } from './DisplayBlock.style'
import { ChecklistDisplayBlock } from './DisplayBlocks/ChecklistDisplayBlock'
import { SwmsGeneralInfoDisplayBlock } from './DisplayBlocks/SwmsGeneralInfoDisplayBlock'
import { SwmsHazardsDisplayBlock } from './DisplayBlocks/SwmsHazardsDisplayBlock'
import { SwmsHrcwDisplayBlock } from './DisplayBlocks/SwmsHrcwDisplayBlock'
import { SwmsPlantDisplayBlock } from './DisplayBlocks/SwmsPlantDisplayBlock'
import { SwmsPpeDisplayBlock } from './DisplayBlocks/SwmsPpeDisplayBlock'
import { DisplayDefects } from './DisplayDefects'
import { DisplayNotes } from './DisplayNotes'
import { DisplayPlatformActions } from './DisplayPlatformActions'

import { useTemplateRenderer } from '@/context/templateRendererContext'

type Props = {
  block: DisplayBlockType
  noMargin?: boolean
}

const getDisplayBlock = (block: DisplayBlockType) => {
  // these are special case blocks that need to be handled specifically
  switch (block.type) {
    case 'checklist':
      return <ChecklistDisplayBlock block={block} /> // radio and checkbox in "display" mode get converted to this
    case 'swmsGeneralInfo':
      return <SwmsGeneralInfoDisplayBlock block={block} />
    case 'swmsHrcw':
      return <SwmsHrcwDisplayBlock block={block} />
    case 'swmsPlant':
      return <SwmsPlantDisplayBlock block={block} />
    case 'swmsPpe':
      return <SwmsPpeDisplayBlock block={block} />
    case 'swmsHazards':
      return <SwmsHazardsDisplayBlock block={block} />
  }

  // some blocks have specific display block types and renderers
  const displayBlockConfig = getBlockConfig(block.kind, block.type)
  if (displayBlockConfig) {
    return displayBlockConfig.renderBlock(block)
  }

  // other blocks are just capture blocks that can be rendered in "display mode"
  const captureBlockConfig = getBlockConfig('capture', block.type)
  return captureBlockConfig?.renderDisplayBlock(block) ?? null
}

const renderName = (block: DisplayBlockType) => {
  if (!block.name) return false

  return true
}

const DisplayBlock: React.FC<Props> = ({ block, noMargin }) => {
  const { id, attachments, notes, platformActions, defects } = block
  const { renderOptions, config } = useTemplateRenderer()

  const displayBlock = useMemo(() => getDisplayBlock(block), [block])

  if (!displayBlock) return null

  return (
    <DisplayBlockWrapper noMargin={noMargin} shiftUp={block.type === 'action'}>
      {renderName(block) ? <DisplayName>{block.name}</DisplayName> : null}
      {displayBlock}

      {renderOptions.showNotes && <DisplayNotes notes={notes} />}

      {renderOptions.showAttachments && (
        <DisplayAttachments
          attachments={attachments}
          renderingInPdf={renderOptions.renderingInPdf}
        />
      )}

      {renderOptions.showActions && (
        <DisplayPlatformActions
          actions={platformActions}
          parentId={id}
          renderingInPdf={renderOptions.renderingInPdf}
        />
      )}

      {renderOptions.showDefects && !config?.isPublic && (
        <DisplayDefects
          defects={defects}
          parentId={id}
          renderingInPdf={renderOptions.renderingInPdf}
        />
      )}
    </DisplayBlockWrapper>
  )
}

export default DisplayBlock
